import { z } from "zod";
import { CommonModels } from "@/data/common/common.models";

export namespace EndorsementsModels {
/** 
 * RelationshipEnumSchema 
 * @type { enum }
 * @description Relationship to the person being endorsed. Example: `Colleague`
 */
export const RelationshipEnumSchema = z.enum(["Colleague", "Manager", "Direct Report", "Client", "Mentor", "Peer", "Vendor", "Other"]);
export type RelationshipEnum = z.infer<typeof RelationshipEnumSchema>;
export const RelationshipEnum = RelationshipEnumSchema.enum;

/** 
 * CreateEndorsementDtoSchema 
 * @type { object }
 * @property { string } relationship Relationship to the person being endorsed. Example: `Colleague` 
 * @property { string } content Endorsement content/message. Example: `John is an excellent software developer with strong problem-solving skills.` 
 * @property { string } userId  
 */
export const CreateEndorsementDtoSchema = z.object({ relationship: RelationshipEnumSchema, content: z.string(), userId: z.string() });
export type CreateEndorsementDto = z.infer<typeof CreateEndorsementDtoSchema>;


/** 
 * CreateEndorsementRequestDtoSchema 
 * @type { object }
 * @property { string[] } emails Array of email addresses to request endorsements from. Example: `colleague@example.com,manager@company.com` 
 * @property { string } message Endorsement message. Example: `Could you write me an endorsement?` 
 */
export const CreateEndorsementRequestDtoSchema = z.object({ emails: z.array(z.email()), message: z.string().nullish() });
export type CreateEndorsementRequestDto = z.infer<typeof CreateEndorsementRequestDtoSchema>;


/** 
 * EndorsementPaginationFilterDtoSchema 
 * @type { object }
 * @property { string } userEmail  
 * @property { string } endorserEmail  
 * @property { string } status  
 * @property { boolean } visible  
 */
export const EndorsementPaginationFilterDtoSchema = z.object({ userEmail: z.string().nullable(), endorserEmail: z.string().nullable(), status: z.string().nullable(), visible: z.boolean().nullable() }).partial();
export type EndorsementPaginationFilterDto = z.infer<typeof EndorsementPaginationFilterDtoSchema>;


/** 
 * EndorsementStatsDtoSchema 
 * @type { object }
 * @property { number } totalReceived Total number of endorsements received. Example: `15` 
 * @property { number } approved Number of approved endorsements. Example: `12` 
 * @property { number } pending Number of pending endorsements. Example: `2` 
 * @property { number } given Number of endorsements given. Example: `8` 
 */
export const EndorsementStatsDtoSchema = z.object({ totalReceived: z.number(), approved: z.number(), pending: z.number(), given: z.number() });
export type EndorsementStatsDto = z.infer<typeof EndorsementStatsDtoSchema>;


/** 
 * WriteEndorsementDtoSchema 
 * @type { object }
 * @property { string } relationship Relationship to the person being endorsed. Example: `Colleague` 
 * @property { string } content Endorsement content/message. Example: `John is an excellent software developer with strong problem-solving skills.` 
 */
export const WriteEndorsementDtoSchema = z.object({ relationship: RelationshipEnumSchema, content: z.string() });
export type WriteEndorsementDto = z.infer<typeof WriteEndorsementDtoSchema>;


/** 
 * UpdateEndorsementVisibilityDtoSchema 
 * @type { object }
 * @property { boolean } visible Whether the endorsement should be visible. Example: `true` 
 */
export const UpdateEndorsementVisibilityDtoSchema = z.object({ visible: z.boolean() });
export type UpdateEndorsementVisibilityDto = z.infer<typeof UpdateEndorsementVisibilityDtoSchema>;


/** 
 * RequestResponseSchema 
 * @type { array }
 */
export const RequestResponseSchema = z.array(CommonModels.EndorsementDtoSchema);
export type RequestResponse = z.infer<typeof RequestResponseSchema>;


/** 
 * GetIncomingResponseSchema 
 * @type { object }
 * @property { object[] } order List of order definitions 
 * @property { number } page 1-indexed page number to begin from 
 * @property { string } cursor ID of item to start after 
 * @property { string } nextCursor Cursor for next set of items 
 * @property { number } limit Items per response 
 * @property { number } totalItems Total available items 
 * @property { CommonModels.EndorsementDto[] } items  
 */
export const GetIncomingResponseSchema = z.object({ ...CommonModels.PaginationDtoSchema.shape, ...z.object({ items: z.array(CommonModels.EndorsementDtoSchema).nullable() }).partial().shape });
export type GetIncomingResponse = z.infer<typeof GetIncomingResponseSchema>;


/** 
 * GetOutgoingResponseSchema 
 * @type { object }
 * @property { object[] } order List of order definitions 
 * @property { number } page 1-indexed page number to begin from 
 * @property { string } cursor ID of item to start after 
 * @property { string } nextCursor Cursor for next set of items 
 * @property { number } limit Items per response 
 * @property { number } totalItems Total available items 
 * @property { CommonModels.EndorsementDto[] } items  
 */
export const GetOutgoingResponseSchema = z.object({ ...CommonModels.PaginationDtoSchema.shape, ...z.object({ items: z.array(CommonModels.EndorsementDtoSchema).nullable() }).partial().shape });
export type GetOutgoingResponse = z.infer<typeof GetOutgoingResponseSchema>;


}
